import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  UserSettingsService, 
  CreateUserSettingsDTO, 
  UpdateUserSettingsDTO 
} from "@/features/Users/services/userSettingsService";

/**
 * Hook para obter todas as configurações de usuário
 */
export const useUserSettings = () => {
  return useQuery({
    queryKey: ['user_settings'], 
    queryFn: () => UserSettingsService.getAll(),
    staleTime: 60000, // 1 minuto
  });
};

/**
 * Hook para obter configuração por ID
 * @param id ID da configuração
 */
export const useUserSettingsById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['user_settings', id], 
    queryFn: () => id ? UserSettingsService.getById(id) : null,
    staleTime: 60000, // 1 minuto
    enabled: !!id
  });
};

/**
 * Hook para obter configuração por ID do usuário
 * @param userId ID do usuário
 */
export const useUserSettingsByUserId = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['user_settings_by_user', userId], 
    queryFn: () => userId ? UserSettingsService.getByUserId(userId) : null,
    staleTime: 60000, // 1 minuto
    enabled: !!userId
  });
};

/**
 * Hook para criar configuração de usuário
 */
export const useCreateUserSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateUserSettingsDTO) => UserSettingsService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user_settings'] });
      queryClient.invalidateQueries({ queryKey: ['user_settings_by_user', data.user_id] });
    }
  });
};

/**
 * Hook para atualizar configuração por ID
 */
export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string, data: UpdateUserSettingsDTO }) => 
      UserSettingsService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user_settings'] });
      queryClient.invalidateQueries({ queryKey: ['user_settings', data.id] });
      queryClient.invalidateQueries({ queryKey: ['user_settings_by_user', data.user_id] });
    }
  });
};

/**
 * Hook para atualizar configuração por ID do usuário
 */
export const useUpdateUserSettingsByUserId = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string, data: UpdateUserSettingsDTO }) => 
      UserSettingsService.updateByUserId(userId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user_settings'] });
      queryClient.invalidateQueries({ queryKey: ['user_settings', data.id] });
      queryClient.invalidateQueries({ queryKey: ['user_settings_by_user', data.user_id] });
    }
  });
};

/**
 * Hook para excluir configuração por ID
 */
export const useDeleteUserSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => UserSettingsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user_settings'] });
    }
  });
};

/**
 * Hook para excluir configuração por ID do usuário
 */
export const useDeleteUserSettingsByUserId = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => UserSettingsService.deleteByUserId(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user_settings'] });
    }
  });
};
